import React, { useState } from 'react';
import { Download, ChevronDown, FileText, File, Lock, Unlock } from 'lucide-react';
import StatusBadge from './StatusBadge';
import { API_BASE, authFetch } from '../constants';

function BatchTable({ batches, loading, onSelectBatch, onGenerateCsv, onDownloadCsv, onRefresh, totalCount, isAdmin }) {
  const showingFiltered = totalCount !== undefined && batches.length !== totalCount;
  const [openDropdown, setOpenDropdown] = useState(null);
  const [labResultsAvailability, setLabResultsAvailability] = useState({});

  // Check lab results availability for completed batches
  React.useEffect(() => {
    const checkLabResults = async () => {
      const completedBatches = batches.filter(b =>
        b.status === 'Completed' || b.status === 'Sent to Lab' || b.status === 'Lab Results Received'
      );
      const availability = {};
      
      for (const batch of completedBatches) {
        try {
          const res = await authFetch(`${API_BASE}/batches/${batch.batch_id}/has-lab-results`);
          if (res.ok) {
            const data = await res.json();
            availability[batch.batch_id] = data;
          }
        } catch (err) {
          console.error(`Failed to check lab results for ${batch.batch_id}:`, err);
        }
      }
      
      setLabResultsAvailability(availability);
    };
    
    if (batches.length > 0) {
      checkLabResults();
    }
  }, [batches]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const downloadLabCSV = async (batchId, e) => {
    e.stopPropagation();
    try {
      const res = await authFetch(`${API_BASE}/lab-results/batch/${batchId}/download-csv`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batchId}_lab_results.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download lab CSV');
    }
    setOpenDropdown(null);
  };

  const downloadLabPDF = async (batchId, e) => {
    e.stopPropagation();
    try {
      const res = await authFetch(`${API_BASE}/lab-results/batch/${batchId}/download-pdf`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batchId}_lab_results.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download lab PDF');
    }
    setOpenDropdown(null);
  };

  const downloadKASExport = async (batch, e) => {
    e.stopPropagation();
    try {
      const res = await authFetch(`${API_BASE}/batches/${batch.batch_id}/download-kas-export`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Use display_batch_id for filename
      const displayId = batch.display_batch_id || batch.batch_id;
      a.download = `${displayId}_enriched_results.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download KAS Export');
    }
    setOpenDropdown(null);
  };

  const unlockBatch = async (batchId, e) => {
    e.stopPropagation();
    if (!window.confirm(`Unlock batch ${batchId}? Customer will be able to view their results.`)) {
      return;
    }
    
    try {
      const res = await authFetch(`${API_BASE}/batches/${batchId}/unlock`, { method: 'POST' });
      if (res.ok) {
        alert('Batch unlocked successfully!');
        // Refresh the batch list
        if (onRefresh) {
          onRefresh();
        }
      } else {
        alert('Failed to unlock batch');
      }
    } catch (err) {
      alert('Failed to unlock batch');
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Submission Batches</h2>
          {showingFiltered && (
            <span className="text-sm text-gray-500">
              Showing {batches.length} of {totalCount} batches
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : batches.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          No submissions yet. Click "Submit Samples" to get started.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grower(s)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Samples</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {batches.map((batch, index) => {
                // Determine if row should be greyed out (locked and non-admin)
                const isLockedForUser = batch.locked && !isAdmin;
                
                return (
                <tr
                  key={batch.batch_id}
                  className={`cursor-pointer transition-colors ${
                    isLockedForUser 
                      ? 'bg-gray-100 opacity-60'
                      : index % 2 === 0 
                        ? 'hover:bg-gray-50' 
                        : 'bg-green-50/80 hover:bg-green-100/70'
                  }`}
                  onClick={() => onSelectBatch(batch.batch_id)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      {batch.locked && (
                        <Lock size={14} className="text-gray-500" title="Locked - Pending Release" />
                      )}
                      {batch.display_batch_id || batch.full_batch_id || batch.batch_id}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{batch.company_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {batch.grower_names || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{batch.sample_count}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(batch.submission_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    {batch.locked && batch.locked_reason ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Lock size={12} />
                        {batch.locked_reason}
                      </span>
                    ) : (
                      <StatusBadge status={(() => {
                        if (!isAdmin) {
                          const labRes = labResultsAvailability[batch.batch_id];
                          // Unlocked batch with lab results → always show Completed
                          if (!batch.locked && labRes?.has_results) return 'Completed';
                          // Any intermediate processing stage → show In Progress
                          if (['Ready to Ship', 'CSV Generated', 'Sent to Lab'].includes(batch.status)) return 'In Progress';
                        }
                        return batch.status;
                      })()} />
                    )}
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2 relative">
                      {/* Show unlock icon for admins on locked batches */}
                      {isAdmin && batch.locked && (
                        <button
                          onClick={(e) => unlockBatch(batch.batch_id, e)}
                          className="text-orange-500 hover:text-orange-700 transition"
                          title="Unlock batch to release results to customer"
                        >
                          <Unlock size={16} />
                        </button>
                      )}
                      
                      {(() => {
                        const isKasInternal = batch.company_name === 'KAS Internal';
                        const labResults = labResultsAvailability[batch.batch_id];
                        
                        // Non-admin users see nothing for locked batches
                        if (!isAdmin && batch.locked) {
                          return (
                            <span className="text-gray-400 text-sm italic">
                              Locked
                            </span>
                          );
                        }
                        
                        // Show dropdown for unlocked batches with lab results (admin can still download even if locked)
                        if (!batch.locked && labResults?.has_results) {
                          return (
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdown(openDropdown === batch.batch_id ? null : batch.batch_id);
                                }}
                                className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm font-medium px-3 py-1 rounded-md hover:bg-green-50 transition"
                              >
                                <Download size={16} />
                                Download
                                <ChevronDown size={16} />
                              </button>
                              
                              {openDropdown === batch.batch_id && (
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                                  {labResults.has_csv && (
                                    <button
                                      onClick={(e) => downloadLabCSV(batch.batch_id, e)}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                                    >
                                      <FileText size={16} className="text-green-600" />
                                      Download Lab CSV
                                    </button>
                                  )}
                                  {labResults.has_pdf && (
                                    <button
                                      onClick={(e) => downloadLabPDF(batch.batch_id, e)}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                                    >
                                      <File size={16} className="text-red-600" />
                                      Download Lab PDF
                                    </button>
                                  )}
                                  {isAdmin && (
                                    <button
                                      onClick={(e) => downloadKASExport(batch, e)}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition border-t border-gray-200"
                                    >
                                      <Download size={16} className="text-blue-600" />
                                      KAS Export (Desktop)
                                    </button>
                                  )}
                                  {!labResults.has_csv && !labResults.has_pdf && (
                                    <div className="px-4 py-2 text-sm text-gray-500">
                                      No files available
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }
                        
                        // For KAS Internal batches (admin's own company), use old simple workflow
                        if (isKasInternal && isAdmin) {
                          if (!batch.csv_generated) {
                            return (
                              <button
                                onClick={() => onGenerateCsv(batch.batch_id)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                Generate CSV
                              </button>
                            );
                          } else if (batch.downloaded) {
                            return (
                              <span className="flex items-center gap-1 text-gray-500 text-sm font-medium">
                                <Download size={16} />
                                Downloaded
                              </span>
                            );
                          } else {
                            return (
                              <button
                                onClick={() => onDownloadCsv(batch.batch_id)}
                                className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm font-medium"
                              >
                                <Download size={16} />
                                Download
                              </button>
                            );
                          }
                        }
                        
                        // For other companies' batches, use new controlled workflow
                        // User can click Ready to Send when not yet generated (including Pending status)
                        if (!isAdmin && !batch.csv_generated) {
                          return (
                            <button
                              onClick={() => onGenerateCsv(batch.batch_id)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Ready to Send
                            </button>
                          );
                        }
                        
                        // Admin sees nothing for non-KAS Internal batches until status is Ready to Ship
                        if (isAdmin && !batch.csv_generated) {
                          return <span className="text-gray-400 text-sm">-</span>;
                        }
                        
                        // Admin can download when CSV generated and status is Ready to Ship
                        if (isAdmin && batch.csv_generated && batch.status === 'Ready to Ship') {
                          return (
                            <button
                              onClick={() => onDownloadCsv(batch.batch_id)}
                              className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm font-medium"
                            >
                              <Download size={16} />
                              Download
                            </button>
                          );
                        }
                        
                        // Show Downloaded status when admin has downloaded
                        if (isAdmin && batch.downloaded && batch.status === 'CSV Generated') {
                          return (
                            <span className="flex items-center gap-1 text-gray-500 text-sm font-medium">
                              <Download size={16} />
                              Downloaded
                            </span>
                          );
                        }
                        
                        // User sees In Progress after clicking Ready to Send
                        if (!isAdmin && batch.csv_generated && batch.status === 'Ready to Ship') {
                          return (
                            <span className="text-blue-600 text-sm font-medium">
                              In Progress
                            </span>
                          );
                        }
                        
                        // User sees In Progress after admin downloads
                        if (!isAdmin && batch.status === 'CSV Generated') {
                          return (
                            <span className="text-blue-600 text-sm font-medium">
                              In Progress
                            </span>
                          );
                        }
                        
                        // User sees In Progress for Sent to Lab status (no lab results yet)
                        if (!isAdmin && batch.status === 'Sent to Lab') {
                          return (
                            <span className="text-blue-600 text-sm font-medium">
                              In Progress
                            </span>
                          );
                        }
                        
                        return null;
                      })()}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BatchTable;

